import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Modal, Badge, Spinner, ButtonGroup } from 'react-bootstrap';
import { FaFileExcel, FaFileWord, FaEye, FaSearch, FaChartBar, FaDownload, FaFileContract } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import api from '../services/api';

const ReportsPage = () => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(null);
    const [statistics, setStatistics] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);

    const [filters, setFilters] = useState({
        search: '',
        dateFrom: '',
        dateTo: '',
        status: ''
    });

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
    });

    useEffect(() => {
        fetchReports();
        fetchStatistics();
    }, [filters, pagination.page]);

    const fetchReports = async () => {
        try {
            // Use the api instance instead of axios directly
            const response = await api.get('/reports', {
                params: {
                    ...filters,
                    page: pagination.page,
                    limit: pagination.limit
                }
            });

            setReports(response.data.reports);
            setPagination(response.data.pagination);
        } catch (error) {
            console.error('Raporlar yüklenirken hata:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchStatistics = async () => {
        try {
            // Use the api instance instead of axios directly
            const response = await api.get('/reports/statistics');
            setStatistics(response.data.statistics);
        } catch (error) {
            console.error('İstatistikler yüklenirken hata:', error);
        }
    };

    const handleDownload = async (reportId) => {
        try {
            setDownloading(`${reportId}-word`);
            // Sadece Word formatı için istek at
            const response = await api.get(`/reports/${reportId}/download?format=word`, {
                responseType: 'blob'
            });

            const contentType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

            // Dosyayı indir
            const blob = new Blob([response.data], { type: contentType });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `rapor-${reportId}.docx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('İndirme hatası:', error);
            alert('Dosya indirilirken hata oluştu');
        } finally {
            setDownloading(null);
        }
    };

    // Yeni senet fonksiyonu - ÇALIŞAN VERSİYON
    const handleSenetView = (reportId) => {
        try {
            // LocalStorage'dan token al
            const token = localStorage.getItem('token');

            if (!token) {
                alert('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.');
                return;
            }

            // BACKEND PORTU (5000) ile URL oluştur
            const senetUrl = `/api/reports/${reportId}/senet?token=${token}`;
            window.open(senetUrl, '_blank', 'noopener,noreferrer');
        } catch (error) {
            console.error('Senet açılırken hata:', error);
            alert('Senet sayfası açılırken hata oluştu');
        }
    };

    const handleViewDetails = async (reportId) => {
        try {
            // Use the api instance instead of axios directly
            const response = await api.get(`/reports/${reportId}`);
            setSelectedReport(response.data.report);
            setShowDetailModal(true);
        } catch (error) {
            console.error('Rapor detayları alınırken hata:', error);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0
        }).format(amount);
    };

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                </Spinner>
            </Container>
        );
    }

    return (
        <Container className="py-5">
            <Row className="mb-4">
                <Col md={8}>
                    <h1>Raporlar</h1>
                    <p className="text-muted">Tüm finansman raporlarınızı görüntüleyin ve yönetin</p>
                </Col>
                <Col md={4} className="text-end">
                    <Link to="/report-generator" className="btn btn-primary">
                        <FaFileWord className="me-2" />
                        Yeni Rapor Oluştur
                    </Link>
                </Col>
            </Row>

            {/* İstatistikler */}
            {statistics && (
                <Row className="mb-4">
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <h3 className="text-primary">{statistics.overview?.totalReports || 0}</h3>
                                <p className="text-muted mb-0">Toplam Rapor</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <h5 className="text-success">{formatCurrency(statistics.overview?.totalLoanAmount || 0)}</h5>
                                <p className="text-muted mb-0">Toplam Kredi</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <h5 className="text-info">{formatCurrency(statistics.overview?.totalVehicleValue || 0)}</h5>
                                <p className="text-muted mb-0">Toplam Araç Değeri</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <h5 className="text-warning">%{statistics.overview?.avgInterestRate?.toFixed(2) || 0}</h5>
                                <p className="text-muted mb-0">Ortalama Faiz</p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Filtreler */}
            <Card className="mb-4">
                <Card.Body>
                    <Form>
                        <Row>
                            <Col md={3}>
                                <Form.Control
                                    type="text"
                                    placeholder="Ara (müşteri, araç, plaka)"
                                    value={filters.search}
                                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                />
                            </Col>
                            <Col md={3}>
                                <Form.Control
                                    type="date"
                                    placeholder="Başlangıç Tarihi"
                                    value={filters.dateFrom}
                                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                                />
                            </Col>
                            <Col md={3}>
                                <Form.Control
                                    type="date"
                                    placeholder="Bitiş Tarihi"
                                    value={filters.dateTo}
                                    onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                                />
                            </Col>
                            <Col md={3}>
                                <Form.Select
                                    value={filters.status}
                                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                >
                                    <option value="">Tüm Durumlar</option>
                                    <option value="completed">Tamamlandı</option>
                                    <option value="draft">Taslak</option>
                                    <option value="cancelled">İptal</option>
                                </Form.Select>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {/* Rapor Listesi */}
            <Card>
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table hover className="mb-0">
                            <thead>
                                <tr>
                                    <th>Tarih</th>
                                    <th>Müşteri</th>
                                    <th>Araç</th>
                                    <th>Kredi Tutarı</th>
                                    <th>Vade</th>
                                    <th>Faiz</th>
                                    <th>İşlemler</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reports.length > 0 ? (
                                    reports.map(report => (
                                        <tr key={report._id}>
                                            <td>{new Date(report.createdAt).toLocaleDateString('tr-TR')}</td>
                                            <td>
                                                <div>
                                                    <strong>{report.customerData?.name}</strong>
                                                    <br />
                                                    <small className="text-muted">{report.customerData?.phone}</small>
                                                </div>
                                            </td>
                                            <td>
                                                <div>
                                                    <strong>{report.vehicleData?.make} {report.vehicleData?.model}</strong>
                                                    <br />
                                                    <small className="text-muted">
                                                        {report.vehicleData?.year} - {report.vehicleData?.plate || 'Plaka yok'}
                                                    </small>
                                                </div>
                                            </td>
                                            <td>{formatCurrency(report.financialData?.loanAmount || 0)}</td>
                                            <td>{report.financialData?.term} ay</td>
                                            <td>%{report.financialData?.interestRate}</td>
                                            <td>
                                                <ButtonGroup size="sm">
                                                    <Button
                                                        variant="info"
                                                        onClick={() => handleViewDetails(report._id)}
                                                        title="Detayları Görüntüle"
                                                    >
                                                        <FaEye />
                                                    </Button>

                                                    <Button
                                                        variant="primary"
                                                        disabled={downloading === `${report._id}-word`}
                                                        onClick={() => handleDownload(report._id)}
                                                        title="Word İndir"
                                                    >
                                                        {downloading === `${report._id}-word` ? (
                                                            <Spinner size="sm" animation="border" />
                                                        ) : (
                                                            <FaFileWord />
                                                        )}
                                                    </Button>

                                                    <Button
                                                        variant="warning"
                                                        onClick={() => handleSenetView(report._id)}
                                                        title="Senet Görüntüle"
                                                    >
                                                        <FaFileContract />
                                                    </Button>
                                                </ButtonGroup>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center py-4">
                                            Henüz rapor oluşturulmamış
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            {/* Detay Modal */}
            <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Rapor Detayları</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedReport && (
                        <Row>
                            <Col md={4}>
                                <h5>Müşteri Bilgileri</h5>
                                <p><strong>Ad Soyad:</strong> {selectedReport.customerData?.name}</p>
                                <p><strong>Telefon:</strong> {selectedReport.customerData?.phone}</p>
                                <p><strong>Email:</strong> {selectedReport.customerData?.email}</p>
                                {selectedReport.customerData?.tcNo && (
                                    <p><strong>TC No:</strong> {selectedReport.customerData.tcNo}</p>
                                )}
                                {selectedReport.customerData?.address && (
                                    <p><strong>Adres:</strong> {selectedReport.customerData.address}</p>
                                )}
                            </Col>
                            <Col md={4}>
                                <h5>Araç Bilgileri</h5>
                                <p><strong>Marka:</strong> {selectedReport.vehicleData?.make} </p>
                                <p><strong>Model:</strong> {selectedReport.vehicleData?.model}</p>
                                <p><strong>Fiyat:</strong> {formatCurrency(selectedReport.vehicleData?.price || 0)}</p>
                                <p><strong>Plaka:</strong> {selectedReport.vehicleData?.plate || '-'}</p>
                                <p><strong>Şase No:</strong> {selectedReport.vehicleData?.saseNo || '-'}</p>
                                <p><strong>Motor No:</strong> {selectedReport.vehicleData?.motorNo || '-'}</p>
                                <p><strong>Tramer:</strong> {selectedReport.vehicleData?.tramer || '-'}</p>
                            </Col>
                            <Col md={4}>
                                <h5>Finansman Detayları</h5>
                                <p><strong>Peşinat:</strong> {formatCurrency(selectedReport.financialData?.downPayment || 0)}</p>
                                <p><strong>Kredi Tutarı:</strong> {formatCurrency(selectedReport.financialData?.loanAmount || 0)}</p>
                                <p><strong>Vade:</strong> {selectedReport.financialData?.term} ay</p>
                                <p><strong>Faiz Oranı:</strong> %{selectedReport.financialData?.interestRate}</p>
                                <p><strong>Aylık Taksit:</strong> {formatCurrency(selectedReport.financialData?.monthlyPayment || 0)}</p>
                                <p><strong>Toplam Ödeme:</strong> {formatCurrency(selectedReport.financialData?.totalPayment || 0)}</p>
                            </Col>
                        </Row>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                        Kapat
                    </Button>

                    <Button
                        variant="primary"
                        onClick={() => handleDownload(selectedReport?._id)}
                        disabled={!selectedReport || downloading === `${selectedReport?._id}-word`}
                    >
                        <FaFileWord className="me-2" />
                        Word İndir
                    </Button>

                    <Button
                        variant="warning"
                        onClick={() => handleSenetView(selectedReport?._id)}
                        disabled={!selectedReport}
                    >
                        <FaFileContract className="me-2" />
                        Senet Görüntüle
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default ReportsPage;
